import { Modal, Form, Input, InputNumber, DatePicker } from "antd";
import dayjs from "dayjs"
import React, { useState } from "react";
import type { CarpoolDirection, CarpoolRole } from "../types";
import type { CarpoolStore } from "../store";

const FESTIVAL_VENUE = "Burg Waldeck";

const MODAL_TITLE: Record<CarpoolRole, string> = {
  driver: "Fahrt anbieten",
  rider: "Mitfahrt gesucht",
  train: "Zugverbindung eintragen",
};

const OK_TEXT: Record<CarpoolRole, string> = {
  driver: "Anbieten",
  rider: "Eintragen",
  train: "Eintragen",
};

const EMAIL_EXTRA: Record<CarpoolRole, string> = {
  driver: "Wird nicht öffentlich angezeigt. Mitfahranfragen werden an diese Adresse gesendet.",
  rider: "Wird nicht öffentlich angezeigt. Fahrtangebote werden an diese Adresse gesendet.",
  train: "Wird nicht öffentlich angezeigt. Verbindungsanfragen werden an diese Adresse gesendet.",
};

interface Props {
  store: CarpoolStore;
  direction: CarpoolDirection;
  role: CarpoolRole;
  open: boolean;
  onClose: () => void;
}

export const CreateCarpoolModal = ({ store, direction, role, open, onClose }: Props) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const isTo = direction === "to";
  const needsExactTime = role !== "rider";
  const showSeats = role === "driver";
  const showFreeTextField = role !== "rider";
  const locationLabel = isTo ? "Abfahrtsort" : "Zielort";
  const locationField = isTo ? "origin" : "destination";

  const handleOk = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      await store.createCarpool({
        direction,
        role,
        driverName: values.driverName,
        driverEmail: values.driverEmail,
        origin: isTo ? values[locationField] : FESTIVAL_VENUE,
        destination: isTo ? FESTIVAL_VENUE : values[locationField],
        seats: showSeats ? values.seats : undefined,
        departureTime: needsExactTime ? values.departureTime.toDate() : values.departureDate.toDate(),
        carType: showFreeTextField ? values.carType || undefined : undefined,
      });
      form.resetFields();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={MODAL_TITLE[role]}
      okText={OK_TEXT[role]}
      cancelText="Abbrechen"
      confirmLoading={submitting}
      onOk={handleOk}
      onCancel={() => { form.resetFields(); onClose(); }}
    >
      <Form form={form} layout="vertical" initialValues={{ seats: 1 }}>
        <Form.Item
          name="driverName"
          label="Dein Name"
          rules={[{ required: true, message: "Bitte Name eingeben" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="driverEmail"
          label="Deine E-Mail"
          extra={EMAIL_EXTRA[role]}
          rules={[
            { required: true, message: "Bitte E-Mail eingeben" },
            { type: "email", message: "Bitte gültige E-Mail eingeben" },
          ]}
        >
          <Input type="email" />
        </Form.Item>
        <Form.Item
          name={locationField}
          label={locationLabel}
          rules={[{ required: true, message: `Bitte ${locationLabel} eingeben` }]}
        >
          <Input />
        </Form.Item>
        {needsExactTime ? (
          <Form.Item
            name="departureTime"
            label="Abfahrt"
            rules={[{ required: true, message: "Bitte Abfahrtszeit angeben" }]}
          >
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="DD.MM.YYYY HH:mm"
              minDate={dayjs("2026-09-17", "YYYY-MM-DD")}
              maxDate={dayjs("2026-09-21", "YYYY-MM-DD")}
              style={{ width: "100%" }}
              minuteStep={15}
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="departureDate"
            label="Datum"
            rules={[{ required: true, message: "Bitte Datum angeben" }]}
          >
            <DatePicker
              format="DD.MM.YYYY"
              minDate={dayjs("2026-09-17", "YYYY-MM-DD")}
              maxDate={dayjs("2026-09-21", "YYYY-MM-DD")}
              style={{ width: "100%" }}
            />
          </Form.Item>
        )}
        {showSeats && (
          <Form.Item
            name="seats"
            label="Freie Plätze"
            rules={[{ required: true, message: "Bitte Anzahl eingeben" }]}
          >
            <InputNumber min={1} max={20} style={{ width: "100%" }} />
          </Form.Item>
        )}
        {showFreeTextField && (
          <Form.Item
            name="carType"
            label={role === "train" ? "Zug/Linie (optional)" : "Auto (optional)"}
          >
            <Input placeholder={role === "train" ? "z.B. ICE 123" : "z.B. VW Golf, silber"} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
